import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api, endpoints } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as GroupIcon } from '../../icons/groups.svg';
import './OrganizationsPage.css';

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
  </svg>
);

const OrganizationsPage = () => {
  const { logout } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    subdomain: '',
    plan: 'free',
    max_users: 10,
    max_templates: 5,
    is_active: true,
    graph_tenant_id: '',
    graph_client_id: '',
    graph_client_secret: '',
    billing_email: '',
    admin_email: ''
  });

  useEffect(() => {
    fetchOrganizations();
  }, []);

  // "Müşteri hesabı aç" on a demo request lands here with the lead's details.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;
    const slug = (prefill.name || '').toLowerCase()
      .replace(/[çğıöşü]/g, (ch) => ({ ç: 'c', ğ: 'g', ı: 'i', ö: 'o', ş: 's', ü: 'u' }[ch]))
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50);
    setFormData((prev) => ({ ...prev, ...prefill, subdomain: slug }));
    setShowCreateModal(true);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(endpoints.admin.organizations.list);
      const orgsData = response.results || response || [];
      setOrganizations(orgsData);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError(err.response?.data?.error || 'Organizasyonlar yüklenemedi');
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  // DRF returns either {error: "..."} (hand-written view errors) or a
  // field-error dict like {admin_email: ["..."], plan: ["..."]} (serializer
  // validation) — the latter was previously swallowed into a useless generic
  // fallback message, leaving no way to tell what was actually wrong.
  const extractErrorMessage = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (data.error) return data.error;
    return Object.entries(data).map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`).join(' | ') || fallback;
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    try {
      await api.post(endpoints.admin.organizations.list, formData);
      setShowCreateModal(false);
      resetForm();
      fetchOrganizations();
    } catch (err) {
      console.error('Error creating organization:', err);
      setError(extractErrorMessage(err, 'Organizasyon oluşturulamadı'));
    }
  };

  const handleUpdateOrg = async (e) => {
    e.preventDefault();
    try {
      await api.patch(
        endpoints.admin.organizations.detail(selectedOrg.id),
        formData
      );
      setShowEditModal(false);
      resetForm();
      setSelectedOrg(null);
      fetchOrganizations();
    } catch (err) {
      console.error('Error updating organization:', err);
      setError(extractErrorMessage(err, 'Organizasyon güncellenemedi'));
    }
  };

  const handleToggleActive = async (org) => {
    try {
      await api.patch(endpoints.admin.organizations.detail(org.id), {
        is_active: !org.is_active
      });
      fetchOrganizations();
    } catch (err) {
      console.error('Error toggling organization status:', err);
      setError(err.response?.data?.error || 'Durum güncellenemedi');
    }
  };

  const handleDeleteOrg = async () => {
    if (!selectedOrg) return;
    try {
      await api.delete(endpoints.admin.organizations.detail(selectedOrg.id));
      setShowDeleteModal(false);
      setSelectedOrg(null);
      fetchOrganizations();
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err.response?.data?.error || 'Organizasyon silinemedi');
      setShowDeleteModal(false);
    }
  };

  const openDeleteModal = (org) => {
    setSelectedOrg(org);
    setShowDeleteModal(true);
  };

  const openEditModal = (org) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      subdomain: org.subdomain,
      plan: org.plan || 'free',
      max_users: org.max_users || 10,
      max_templates: org.max_templates || 5,
      is_active: org.is_active,
      graph_tenant_id: org.graph_tenant_id || '',
      graph_client_id: org.graph_client_id || '',
      graph_client_secret: '',
      billing_email: org.billing_email || ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      subdomain: '',
      plan: 'free',
      max_users: 10,
      max_templates: 5,
      is_active: true,
      graph_tenant_id: '',
      graph_client_id: '',
      graph_client_secret: '',
      billing_email: '',
      admin_email: ''
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Statistics
  const stats = {
    total: organizations.length,
    active: organizations.filter(o => o.is_active).length,
    inactive: organizations.filter(o => !o.is_active).length,
    totalUsers: organizations.reduce((sum, o) => sum + (o.current_usage?.users || 0), 0)
  };

  // Filtered organizations
  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    org.subdomain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <h1>Organizasyon Yönetimi</h1>
          <p className="page-header-subtitle">Platformdaki tüm organizasyonlar</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={fetchOrganizations}>
            ↻ Yenile
          </button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            + Yeni Organizasyon
          </button>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <WarningIcon />
          <span>{error}</span>
          <button onClick={() => setError(null)}>Kapat</button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <p className="stat-value">{stats.total}</p>
          <h3>Toplam Organizasyon</h3>
        </div>
        <div className="stat-card stat-card-positive">
          <p className="stat-value">{stats.active}</p>
          <h3>Aktif</h3>
        </div>
        <div className="stat-card stat-card-neutral">
          <p className="stat-value">{stats.inactive}</p>
          <h3>Pasif</h3>
        </div>
        <div className="stat-card stat-card-dark">
          <p className="stat-value">{stats.totalUsers}</p>
          <h3>Toplam Kullanıcı</h3>
        </div>
      </div>

      {/* Table Container */}
      <div className="table-container">
        <div className="table-header">
          <h2>Tüm Organizasyonlar</h2>
          <div className="search-box">
            <input
              type="text"
              placeholder="Organizasyon ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredOrgs.length === 0 ? (
          <div className="empty-state">
            <GroupIcon />
            <h3>Organizasyon Bulunamadı</h3>
            <p>Henüz hiç organizasyon oluşturulmamış</p>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              İlk Organizasyonu Oluştur
            </button>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Organizasyon</th>
                <th>Alt Domain</th>
                <th>Plan</th>
                <th>Kullanıcılar</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.map(org => (
                <tr key={org.id}>
                  <td>
                    <div className="org-name">
                      <GroupIcon />
                      {org.name}
                    </div>
                  </td>
                  <td>
                    <span className="subdomain-badge">{org.subdomain}</span>
                    {org.customer_number && (
                      <div className="customer-number-hint">{org.customer_number}</div>
                    )}
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{org.plan || 'Free'}</td>
                  <td>
                    {org.current_usage?.users || 0} / {org.max_users || 0}
                  </td>
                  <td>
                    <span className={`status-badge ${org.is_active ? 'active' : 'inactive'}`}>
                      <span className="status-indicator"></span>
                      {org.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="action-link" onClick={() => openEditModal(org)}>
                        Düzenle
                      </button>
                      <button
                        className="btn-icon delete"
                        onClick={() => openDeleteModal(org)}
                        title="Sil"
                      >
                        <TrashIcon />
                      </button>
                      <button
                        className={`btn btn-toggle btn-${org.is_active ? 'secondary' : 'success'}`}
                        onClick={() => handleToggleActive(org)}
                      >
                        {org.is_active ? 'Devre Dışı Bırak' : 'Aktif Et'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Yeni Organizasyon Oluştur</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateOrg}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Organizasyon Adı *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Örn: Acme Corporation"
                  />
                </div>
                <div className="form-group">
                  <label>Alt Domain *</label>
                  <input
                    type="text"
                    name="subdomain"
                    value={formData.subdomain}
                    onChange={handleChange}
                    required
                    placeholder="Örn: acme"
                  />
                  <small className="form-group-hint">
                    Dahili kısa ad, benzersiz olmalı (adreslerde kullanılmaz)
                  </small>
                </div>
                <div className="form-group">
                  <label>Plan</label>
                  <select name="plan" value={formData.plan} onChange={handleChange}>
                    <option value="free">Ücretsiz</option>
                    <option value="starter">Başlangıç</option>
                    <option value="business">İş</option>
                    <option value="enterprise">Kurumsal</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Maksimum Kullanıcı</label>
                  <input
                    type="number"
                    name="max_users"
                    value={formData.max_users}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Maksimum Şablon</label>
                  <input
                    type="number"
                    name="max_templates"
                    value={formData.max_templates}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Faturalandırma E-posta</label>
                  <input
                    type="email"
                    name="billing_email"
                    value={formData.billing_email}
                    onChange={handleChange}
                    placeholder="billing@company.com"
                  />
                </div>
                <div className="form-group">
                  <label>Yönetici E-postası *</label>
                  <input
                    type="email"
                    name="admin_email"
                    value={formData.admin_email}
                    onChange={handleChange}
                    required
                    placeholder="admin@company.com"
                  />
                  <small className="form-group-hint">
                    Organizasyonun ilk org_admin hesabı bu e-posta ile oluşturulur; şifre yoktur,
                    yönetici bu adrese ait Microsoft hesabıyla giriş yapar. E-posta domain'i
                    (herkese açık sağlayıcılar hariç) organizasyona SSO ile katılım için tanımlanır.
                  </small>
                </div>
                <div className="form-group">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      id="is_active_create"
                    />
                    <label htmlFor="is_active_create">
                      Organizasyonu aktif olarak oluştur
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Oluştur
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedOrg && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Organizasyonu Düzenle</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateOrg}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Organizasyon Adı *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Alt Domain *</label>
                  <input
                    type="text"
                    name="subdomain"
                    value={formData.subdomain}
                    onChange={handleChange}
                    required
                    disabled
                  />
                  <small className="form-group-hint">
                    Alt domain değiştirilemez
                  </small>
                </div>
                <div className="form-group">
                  <label>Plan</label>
                  <select name="plan" value={formData.plan} onChange={handleChange}>
                    <option value="free">Ücretsiz</option>
                    <option value="starter">Başlangıç</option>
                    <option value="business">İş</option>
                    <option value="enterprise">Kurumsal</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Maksimum Kullanıcı</label>
                  <input
                    type="number"
                    name="max_users"
                    value={formData.max_users}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Maksimum Şablon</label>
                  <input
                    type="number"
                    name="max_templates"
                    value={formData.max_templates}
                    onChange={handleChange}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Faturalandırma E-posta</label>
                  <input
                    type="email"
                    name="billing_email"
                    value={formData.billing_email}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Microsoft Tenant ID</label>
                  <input
                    type="text"
                    name="graph_tenant_id"
                    value={formData.graph_tenant_id}
                    onChange={handleChange}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label>Microsoft Client ID</label>
                  <input
                    type="text"
                    name="graph_client_id"
                    value={formData.graph_client_id}
                    onChange={handleChange}
                    placeholder="Optional"
                  />
                </div>
                <div className="form-group">
                  <label>Microsoft Client Secret</label>
                  <input
                    type="password"
                    name="graph_client_secret"
                    value={formData.graph_client_secret}
                    onChange={handleChange}
                    placeholder="Boş bırakırsanız değişmez"
                  />
                </div>
                <div className="form-group">
                  <div className="checkbox-wrapper">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleChange}
                      id="is_active_edit"
                    />
                    <label htmlFor="is_active_edit">
                      Organizasyon aktif
                    </label>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  İptal
                </button>
                <button type="submit" className="btn btn-primary">
                  Güncelle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedOrg && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><WarningIcon /> Organizasyonu Sil</h2>
              <button className="close-btn" onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-danger-notice">
                <WarningIcon />
                <p>Bu işlem geri alınamaz!</p>
              </div>
              <p className="modal-delete-summary">
                <strong>{selectedOrg.name}</strong> organizasyonunu silmek üzeresiniz.
              </p>
              <p className="modal-delete-summary">
                Bu işlem şunları silecektir:
              </p>
              <ul className="modal-delete-list">
                <li>Tüm kullanıcılar ({selectedOrg.current_usage?.users || 0} kullanıcı)</li>
                <li>Tüm imza şablonları</li>
                <li>Tüm grup atamaları</li>
                <li>Organizasyon ayarları</li>
                <li>İlişkili tüm veriler</li>
              </ul>
              <p className="modal-delete-confirm">
                Silmek istediğinizden emin misiniz?
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                İptal
              </button>
              <button type="button" className="btn btn-danger" onClick={handleDeleteOrg}>
                <TrashIcon /> Evet, Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationsPage;
